import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wallet, Package, MapPin, Heart, LogOut, ArrowUpRight, ArrowDownLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { OrderCard } from "@/components/OrderCard";

const TABS = [
  { key: "orders", label: "Orders", icon: Package },
  { key: "wallet", label: "Wallet", icon: Wallet },
  { key: "addresses", label: "Addresses", icon: MapPin },
];

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";
  const setTab = (t) => setParams({ tab: t }, { replace: true });
  const [wallet, setWallet] = useState(null);
  const [brands, setBrands] = useState([]);
  const [orders, setOrders] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [addr, setAddr] = useState({ name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  const loadAddresses = () => api.get("/addresses").then((r) => setAddresses(r.data)).catch(() => {});

  useEffect(() => {
    if (user) {
      api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
      api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
      api.get("/my-orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
      loadAddresses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveAddress = async (e) => {
    e.preventDefault();
    try {
      await api.post("/addresses", addr);
      toast.success("Address saved");
      setAddr({ name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });
      setShowAddrForm(false);
      loadAddresses();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeAddress = async (id) => {
    await api.delete(`/addresses/${id}`).catch(() => {});
    loadAddresses();
  };

  if (!user) return null;

  return (
    <main data-testid="account-page">
      <Navbar />
      <section className="pt-32 md:pt-40 pb-24 min-h-screen">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-xs uppercase tracking-[0.5em] text-gold mb-4">My Account</p>
            <h1 className="font-serif font-medium text-4xl sm:text-5xl text-zinc-900 tracking-tight" data-testid="account-name">
              Hello, <em className="text-gold">{user.name || user.email.split("@")[0]}</em>
            </h1>
            <p className="text-zinc-500 text-sm mt-3">{user.email}</p>
          </motion.div>

          <div className="flex flex-wrap items-center gap-2 mt-12 border-b border-zinc-200" data-testid="account-tabs">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={`account-tab-${key}`}
                className={`flex items-center gap-2 px-5 py-3.5 text-[11px] uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors duration-300 ${
                  tab === key ? "border-gold text-gold" : "border-transparent text-zinc-400 hover:text-zinc-700"
                }`}
              >
                <Icon size={14} /> {label}
                {key === "orders" && orders?.length > 0 && <span className="text-[9px] bg-pink-50 text-gold px-1.5 py-0.5">{orders.length}</span>}
              </button>
            ))}
            <Link to="/wishlist" className="ml-auto hidden sm:flex items-center gap-2 px-5 py-3.5 text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-gold transition-colors duration-300" data-testid="account-wishlist-link">
              <Heart size={14} /> Wishlist
            </Link>
          </div>

          {tab === "orders" && (
            <div className="mt-10" data-testid="account-orders-panel">
              {orders === null ? (
                <p className="text-zinc-400 text-xs uppercase tracking-[0.4em]">Loading…</p>
              ) : orders.length === 0 ? (
                <div className="text-center py-20" data-testid="account-orders-empty">
                  <p className="font-serif italic text-3xl text-zinc-400">No orders yet.</p>
                  <Link to="/shop" className="inline-block mt-8 bg-gold text-white text-xs uppercase tracking-[0.3em] px-10 py-4 hover:bg-champagne transition-colors duration-300">
                    Start Shopping
                  </Link>
                </div>
              ) : (
                <div className="space-y-6">
                  {orders.map((o) => <OrderCard key={o.id} o={o} />)}
                </div>
              )}
            </div>
          )}

          {tab === "wallet" && (
            <div className="mt-10" data-testid="account-wallet-panel">
              <div className="border border-gold/30 bg-gradient-to-br from-pink-50 to-white p-8 max-w-md" data-testid="wallet-card">
                <div className="flex items-center gap-3 text-gold">
                  <Wallet size={18} />
                  <span className="text-[10px] uppercase tracking-[0.3em]">ViRa Wallet</span>
                </div>
                <p className="font-serif text-5xl text-zinc-900 mt-6" data-testid="wallet-balance">
                  {wallet ? inr(wallet.balance) : "—"}
                </p>
                <p className="text-xs text-zinc-500 mt-3">Earn {wallet?.cashback_percent ?? 5}% cashback on every order. Pay with wallet at checkout.</p>
              </div>
              {wallet && wallet.transactions.length > 0 && (
                <div className="mt-10" data-testid="wallet-transactions">
                  <p className="text-[10px] uppercase tracking-[0.4em] text-zinc-400 mb-6">Wallet Activity</p>
                  <div className="border border-zinc-200 divide-y divide-zinc-100">
                    {wallet.transactions.map((t) => (
                      <div key={t.id} className="flex items-center gap-4 px-6 py-4" data-testid={`wallet-txn-${t.id}`}>
                        <span className={`p-2 ${t.type === "credit" ? "text-emerald-500" : "text-red-400"}`}>
                          {t.type === "credit" ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}
                        </span>
                        <span className="text-sm text-zinc-600 flex-1">{t.note}</span>
                        <span className={`text-sm font-medium ${t.type === "credit" ? "text-emerald-500" : "text-red-400"}`}>
                          {t.type === "credit" ? "+" : "−"}{inr(t.amount)}
                        </span>
                        <span className="text-xs text-zinc-400 hidden sm:block">{new Date(t.created_at).toLocaleDateString("en-IN")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "addresses" && (
            <div className="mt-10" data-testid="address-book">
              <div className="flex items-center justify-between mb-6">
                <p className="text-[10px] uppercase tracking-[0.4em] text-zinc-400">Address Book</p>
                <button
                  onClick={() => setShowAddrForm(!showAddrForm)}
                  data-testid="add-address-btn"
                  className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-gold hover:text-champagne transition-colors duration-300"
                >
                  <Plus size={13} /> Add Address
                </button>
              </div>
              {showAddrForm && (
                <form onSubmit={saveAddress} className="border border-zinc-200 p-6 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="address-form">
                  {[["name", "Full name"], ["phone", "Phone"], ["line1", "Address line 1"], ["line2", "Address line 2 (optional)"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"]].map(([k, ph]) => (
                    <input
                      key={k}
                      required={k !== "line2"}
                      value={addr[k]}
                      onChange={(e) => setAddr((a) => ({ ...a, [k]: e.target.value }))}
                      placeholder={ph}
                      data-testid={`address-${k}-input`}
                      className={`bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 focus:outline-none focus:border-gold/60 transition-colors duration-300 ${k === "line1" || k === "line2" ? "sm:col-span-2" : ""}`}
                    />
                  ))}
                  <button type="submit" data-testid="address-save-btn" className="sm:col-span-2 bg-gold text-white text-xs uppercase tracking-[0.3em] py-3.5 hover:bg-champagne transition-colors duration-300">
                    Save Address
                  </button>
                </form>
              )}
              {addresses.length === 0 && !showAddrForm ? (
                <p className="text-sm text-zinc-400 font-serif italic" data-testid="address-empty">No saved addresses yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {addresses.map((a) => (
                    <div key={a.id} className="border border-zinc-200 p-5 flex gap-4" data-testid={`address-card-${a.id}`}>
                      <MapPin size={16} className="text-gold shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-800">{a.name} · {a.phone}</p>
                        <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}</p>
                      </div>
                      <button onClick={() => removeAddress(a.id)} data-testid={`address-delete-${a.id}`} className="self-start p-1.5 text-zinc-300 hover:text-red-400 transition-colors duration-200" aria-label="Delete address">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="account-logout-btn"
            className="mt-14 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-red-500 transition-colors duration-300"
          >
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </section>
      <Footer brands={brands} />
    </main>
  );
}
